import { config } from '@gen2/config';
import React, { useEffect, useRef, useState } from 'react';

const SCRIPT_CHECK_INTERVAL_MS = 100;
const MAX_SCRIPT_LOAD_RETRIES = 50; // 5 seconds total (50 * 100ms)

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode?: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      ready?: (callback: () => void) => void;
      getResponse?: (widgetId?: string) => string | undefined;
      isExpired?: (widgetId?: string) => boolean;
      execute?: (widgetId?: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  resetKey?: number; // External reset trigger
}

export const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onSuccess,
  onError,
  onExpire,
  theme = 'auto',
  size = 'flexible',
  resetKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const prevResetKeyRef = useRef(resetKey);

  // Check if Turnstile script is loaded
  // Using turnstile.ready() when available (explicit rendering mode)
  // Falls back to polling for compatibility
  useEffect(() => {
    if (window.turnstile?.ready) {
      window.turnstile.ready(() => {
        setIsScriptLoaded(true);
      });
      return;
    }

    // Fallback: Poll for script availability
    let retryCount = 0;
    const checkScriptLoaded = () => {
      if (window.turnstile) {
        setIsScriptLoaded(true);
      } else if (retryCount < MAX_SCRIPT_LOAD_RETRIES) {
        retryCount++;
        setTimeout(checkScriptLoaded, SCRIPT_CHECK_INTERVAL_MS);
      } else {
        console.error('Cloudflare Turnstile script failed to load');
      }
    };

    checkScriptLoaded();
  }, []);

  useEffect(() => {
    if (
      !containerRef.current ||
      !window.turnstile ||
      !isScriptLoaded ||
      !config.turnstile.siteKey
    ) {
      if (!config.turnstile.siteKey) {
        console.error(
          'Cloudflare Turnstile site key is not configured. Please set NX_PUBLIC_TURNSTILE_SITE_KEY environment variable.',
        );
        console.error(
          'Current config.turnstile.siteKey value:',
          config.turnstile.siteKey,
        );
      }
      return;
    }

    // Render the Turnstile widget
    const widgetId = window.turnstile.render(containerRef.current, {
      sitekey: config.turnstile.siteKey,
      callback: onSuccess,
      'error-callback': onError,
      'expired-callback': onExpire,
      theme,
      size,
    });

    widgetIdRef.current = widgetId;

    // Cleanup function to remove the widget when component unmounts
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onSuccess, onError, onExpire, theme, size, isScriptLoaded]);

  // Handle external reset via resetKey prop
  useEffect(() => {
    if (
      resetKey !== prevResetKeyRef.current &&
      resetKey > 0 && // Only reset if key is actually set (initial render has key=0)
      widgetIdRef.current &&
      window.turnstile
    ) {
      window.turnstile.reset(widgetIdRef.current);
      prevResetKeyRef.current = resetKey;
    }
  }, [resetKey]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
};

export default TurnstileWidget;
